import { AgencyAuthSessionV1Schema, type AgencyAuthSessionV1 } from "@enrein/shared-contracts";

export type StudioSessionState = "loading" | "authenticated" | "unauthenticated" | "recoverable-error";

export type StudioSessionUnauthenticatedReason =
  | "missing"
  | "unauthorized"
  | "expired"
  | "revoked"
  | "malformed";

export type StudioSessionRecoverableReason = "network" | "cors" | "server";

export type StudioSessionBootstrapResult =
  | { readonly state: "authenticated"; readonly session: AgencyAuthSessionV1 }
  | { readonly state: "unauthenticated"; readonly reason: StudioSessionUnauthenticatedReason }
  | { readonly state: "recoverable-error"; readonly reason: StudioSessionRecoverableReason };

export function canMountProtectedStudioShell(
  result: StudioSessionBootstrapResult | { readonly state: "loading" },
): boolean {
  return result.state === "authenticated";
}

function isExpiredSession(session: AgencyAuthSessionV1, nowMs: number): boolean {
  const expires = Date.parse(session.expiresAt);
  return Number.isFinite(expires) && expires <= nowMs;
}

function errorText(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }
  return String(error ?? "");
}

export function classifyStudioSessionFailure(error: unknown): StudioSessionBootstrapResult {
  const text = errorText(error);
  if (/malformed|invalid_type|unrecognized_keys|ZodError|session dto/i.test(text)) {
    return { state: "unauthenticated", reason: "malformed" };
  }
  if (/expired/i.test(text)) {
    return { state: "unauthenticated", reason: "expired" };
  }
  if (/revoked/i.test(text)) {
    return { state: "unauthenticated", reason: "revoked" };
  }
  if (/401|unauthorized|agency_session_required/i.test(text)) {
    return { state: "unauthenticated", reason: "unauthorized" };
  }
  if (/cors|access-control-allow-origin/i.test(text)) {
    return { state: "recoverable-error", reason: "cors" };
  }
  if (/failed to fetch|networkerror|network failure|load failed|econnrefused/i.test(text)) {
    return { state: "recoverable-error", reason: "network" };
  }
  return { state: "recoverable-error", reason: "server" };
}

export async function bootstrapStudioSession(input: {
  readonly getSession: () => Promise<AgencyAuthSessionV1 | null>;
  readonly nowMs?: number;
}): Promise<StudioSessionBootstrapResult> {
  try {
    const session = await input.getSession();
    if (!session) {
      return { state: "unauthenticated", reason: "missing" };
    }
    const parsed = AgencyAuthSessionV1Schema.safeParse(session);
    if (!parsed.success) {
      return { state: "unauthenticated", reason: "malformed" };
    }
    if (isExpiredSession(parsed.data, input.nowMs ?? Date.now())) {
      return { state: "unauthenticated", reason: "expired" };
    }
    return { state: "authenticated", session: parsed.data };
  } catch (error) {
    return classifyStudioSessionFailure(error);
  }
}
